import type {
  IPickPageOtherCampaignTargets,
  IPickPageItem,
  IPickPageCurrentCampaignUsage,
  IPickPageRemoveConfirmState,
  IPickPageMode,
  IPickPageModeConfig,
  IPickPageTypeConfig,
  IPickPageType,
} from '../types';
import type { Campaign } from '~/modules/campaign/types';
import type { ScrollableRef } from '@shopify/polaris';

import {
  DEFAULT_PICK_PAGE_SORT,
  PICK_PAGE_MODE,
  PICK_PAGE_MODE_CONFIG,
  PICK_PAGE_SELECT_ALL,
  PICK_PAGE_TYPE_CONFIG,
} from '../constants';
import { findVariantByOrigin, getVariantLabelByType, getVariantType } from '~/modules/campaign';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { isPickPageSelectable, toPickPageMatchKey, toVariantPageKey } from '../utils';
import { usePickPageRemoveConfirm } from '../hooks/usePickPageRemoveConfirm';
import { usePickPageSource } from '../hooks/sources/usePickPageSource';
import { usePickPageSelection } from '../hooks/usePickPageSelection';
import { EOrderDirectionType } from '~/core/gemxql/types/order-by';
import { usePickPageUsage } from '../hooks/usePickPageUsage';
import { useAsyncWithErrorToast } from '~/hooks';

interface IPickPageContext {
  /** What the modal picks — read by rows whose copy names the thing being picked. */
  mode: IPickPageMode;
  modeConfig: IPickPageModeConfig;
  /** Whether the modal was opened for Control, whose page has to be live on the storefront already. */
  isControl: boolean;
  /** Copy of the list on show — the active page type's, or the merged search's while one runs. */
  typeConfig: IPickPageTypeConfig;
  selectedType: IPickPageType;
  handleChangeType: (type: IPickPageType) => void;
  /** A search is spanning every page type, so the list is merged and the type tabs step aside. */
  isSearchAll: boolean;
  /** Reads as "Control" or "Variant" — the variant the modal was opened for. */
  targetLabel: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
  sort: EOrderDirectionType;
  toggleSort: () => void;
  /** Whether the list takes more than one pick — price tests do, URL tests pick a single page. */
  isMultiSelect: boolean;
  selectedIds: string[];
  handleChangeSelectedIds: (ids: string[] | typeof PICK_PAGE_SELECT_ALL) => void;
  handleToggleSelectedId: (id: string) => void;
  filteredPages: IPickPageItem[];
  /** Ids the list is allowed to select — what the header's select-all checkbox reads and writes. */
  selectableIds: string[];
  selectedPages: IPickPageItem[];
  /** Whether the selection moved off those picks — the confirm action waits for it. */
  isSelectionChanged: boolean;
  listScrollRef: React.RefObject<ScrollableRef>;
  isLoading: boolean;
  isError: boolean;
  isSourceEmpty: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  handleFetchNextPage: () => void;
  handleReset: () => void;
  /** Clears filters/selection without refetching — used when the modal closes. */
  handleResetFilters: () => void;
  currentCampaignUsageByPageKey: Record<string, IPickPageCurrentCampaignUsage>;
  otherCampaignTargets: IPickPageOtherCampaignTargets;
  handleRefetchUsage: () => Promise<void>;
  /** Single instance shared by every row — see `handleOpenRemoveConfirm`. */
  removeConfirmState: IPickPageRemoveConfirmState | null;
  removeConfirmDescription: string;
  isRemovingTemplate: boolean;
  handleOpenRemoveConfirm: (params: IPickPageRemoveConfirmState) => void;
  handleCloseRemoveConfirm: () => void;
  handleConfirmRemove: () => Promise<void>;
}

export interface IPickPageProviderProps {
  children: React.ReactNode;
  /** What the modal picks: a product for a price test, any storefront page for a URL test. */
  mode?: IPickPageMode;
  /** Experiment being edited, used to flag the products it already tests. */
  campaign?: Campaign;
  /** Variant the modal was opened for, whose own page is hidden from the list rather than shown disabled. */
  targetVariantId?: string;
  /** Tab the modal opens on — the type the target already holds, so a change starts where the current pick came from. */
  initialType?: IPickPageType;
  /** Lets the list take more than one pick. Off by default: a variant holds a single page. */
  isMultiSelect?: boolean;
  /** Whether the modal is on screen — the picks below are read again every time it opens. */
  isOpen?: boolean;
  /** Picks the target already holds, shown checked so the modal edits them rather than starting over. */
  pickedPages?: IPickPageItem[];
}

const PickPageContext = createContext<IPickPageContext | null>(null);

export const usePickPageContext = () => {
  const context = useContext(PickPageContext);
  if (!context) throw new Error('usePickPageContext must be used within a PickPageProvider');

  return context;
};

export const PickPageProvider: React.FC<IPickPageProviderProps> = ({
  children,
  mode = PICK_PAGE_MODE.PRICE,
  campaign,
  targetVariantId,
  initialType,
  isMultiSelect = false,
  isOpen = false,
  pickedPages,
}) => {
  const modeConfig = PICK_PAGE_MODE_CONFIG[mode];

  // A type the mode doesn't offer has no tab to land on, so the mode's own first type stands in.
  const defaultType =
    initialType && modeConfig.types.includes(initialType) ? initialType : modeConfig.types[0];

  const [selectedType, setSelectedType] = useState<IPickPageType>(defaultType);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState<EOrderDirectionType>(DEFAULT_PICK_PAGE_SORT);
  const listScrollRef = useRef<ScrollableRef>(null);

  const targetVariant = campaign?.variants?.find((variant) => variant?.id === targetVariantId);
  const isControl =
    !!targetVariantId && targetVariantId === findVariantByOrigin(campaign, true)?.id;

  // A typed term searches whatever the mode can hold rather than the active tab alone, so the tabs
  // have nothing to filter while one runs.
  const isSearchAll = !!searchValue.trim() && !!modeConfig.searchAllConfig;
  const typeConfig =
    (isSearchAll && modeConfig.searchAllConfig) || PICK_PAGE_TYPE_CONFIG[selectedType];

  const {
    items,
    isLoading,
    isError,
    isSourceEmpty,
    isFetchingNextPage,
    hasNextPage,
    handleFetchNextPage,
    handleRefresh,
  } = usePickPageSource({
    mode,
    types: modeConfig.types,
    type: selectedType,
    searchValue,
    sort,
    isControl,
    isSearchAll,
  });

  const pageById = useMemo(() => new Map(items.map((page) => [page.id, page])), [items]);
  const resolvePage = useCallback((id: string) => pageById.get(id), [pageById]);

  const {
    selectedIds,
    selectedPages,
    pickedPageKeys,
    isSelectionChanged,
    handleToggleSelectedId,
    handleSetSelectedIds,
    handleResetSelection,
  } = usePickPageSelection({ isOpen, isMultiSelect, pickedPages, resolvePage });

  // Re-offering the page this variant/control already has is redundant, so its own pick is hidden here
  // rather than merely disabled — unlike other variants' picks, which still show disabled with a reason.
  const selfPageKey = targetVariant ? toVariantPageKey(targetVariant) : undefined;

  const filteredPages = useMemo(() => {
    // Only multi-select keeps its own pick listed, so it can be unchecked to drop it.
    if (!selfPageKey || (isMultiSelect && pickedPageKeys.has(selfPageKey))) return items;

    return items.filter(
      (page) => toPickPageMatchKey({ baseID: page.baseID, url: page.url }) !== selfPageKey,
    );
  }, [items, selfPageKey, isMultiSelect, pickedPageKeys]);

  const {
    currentCampaignUsageByPageKey: campaignUsageByPageKey,
    otherCampaignTargets,
    isLoadingUsage,
    handleRefetchUsage,
  } = usePickPageUsage({ campaign, targetVariantId });

  // A page the modal opened on is held by this very target, so the "already tested by this
  // experiment" lock is dropped for it here, at the source: unchecking it is what hands it back, and
  // a row reading the lock would only refuse to let that happen.
  const currentCampaignUsageByPageKey = useMemo(() => {
    if (!pickedPageKeys.size) return campaignUsageByPageKey;

    return Object.fromEntries(
      Object.entries(campaignUsageByPageKey).filter(([key]) => !pickedPageKeys.has(key)),
    );
  }, [campaignUsageByPageKey, pickedPageKeys]);

  const toggleSort = useCallback(() => {
    setSort((current) =>
      current === EOrderDirectionType.ASC ? EOrderDirectionType.DESC : EOrderDirectionType.ASC,
    );
  }, []);

  // What select-all picks, and what the header checkbox measures its own state against.
  const selectableIds = useMemo(
    () =>
      filteredPages
        .filter((page) =>
          isPickPageSelectable({
            page,
            mode,
            isControl,
            currentCampaignUsageByPageKey,
            otherCampaignTargets,
          }),
        )
        .map((page) => page.id),
    [filteredPages, mode, isControl, currentCampaignUsageByPageKey, otherCampaignTargets],
  );

  // Checkbox selection: select-all from the header, the whole new selection from a row. A disabled row
  // can't reach here — its own checkbox is disabled, and select-all only ever offers `selectableIds`.
  const handleChangeSelectedIds = useCallback(
    (ids: string[] | typeof PICK_PAGE_SELECT_ALL) => {
      const nextIds = ids === PICK_PAGE_SELECT_ALL ? selectableIds : ids;

      handleSetSelectedIds(isMultiSelect ? nextIds : nextIds.slice(-1));
    },
    [isMultiSelect, selectableIds, handleSetSelectedIds],
  );

  const scrollListToTop = useCallback(
    () => listScrollRef.current?.scrollTo(0, { behavior: 'instant' }),
    [],
  );

  // Each type has its own list, so a pick made in the previous tab has nothing to stay highlighted on.
  const handleChangeType = useCallback(
    (type: IPickPageType) => {
      setSelectedType(type);
      handleResetSelection();
      scrollListToTop();
    },
    [scrollListToTop, handleResetSelection],
  );

  const handleResetFilters = useCallback(() => {
    setSearchValue('');
    setSort(DEFAULT_PICK_PAGE_SORT);
    handleResetSelection();
    setSelectedType(defaultType);
  }, [defaultType, handleResetSelection]);

  const withErrorToast = useAsyncWithErrorToast();

  const handleReset = useCallback(async () => {
    scrollListToTop();
    await withErrorToast(() => Promise.all([handleRefresh(), handleRefetchUsage()]), {
      source: 'PickPageProvider.handleReset',
      tags: { app: 'App' },
    });
  }, [scrollListToTop, handleRefresh, handleRefetchUsage, withErrorToast]);

  const {
    removeConfirmState,
    removeConfirmDescription,
    isRemovingTemplate,
    handleOpenRemoveConfirm,
    handleCloseRemoveConfirm,
    handleConfirmRemove,
  } = usePickPageRemoveConfirm({ handleRefetchUsage });

  const value = useMemo<IPickPageContext>(
    () => ({
      mode,
      modeConfig,
      isControl,
      typeConfig,
      selectedType,
      handleChangeType,
      isSearchAll,
      targetLabel: getVariantLabelByType(getVariantType(targetVariant)),
      searchValue,
      setSearchValue,
      sort,
      toggleSort,
      isMultiSelect,
      selectedIds,
      handleChangeSelectedIds,
      handleToggleSelectedId,
      filteredPages,
      selectableIds,
      selectedPages,
      isSelectionChanged,
      listScrollRef,
      isLoading: isLoading || isLoadingUsage,
      isError,
      isSourceEmpty,
      isFetchingNextPage,
      hasNextPage,
      handleFetchNextPage,
      handleReset,
      handleResetFilters,
      currentCampaignUsageByPageKey,
      otherCampaignTargets,
      handleRefetchUsage,
      removeConfirmState,
      removeConfirmDescription,
      isRemovingTemplate,
      handleOpenRemoveConfirm,
      handleCloseRemoveConfirm,
      handleConfirmRemove,
    }),
    [
      mode,
      modeConfig,
      isControl,
      typeConfig,
      selectedType,
      handleChangeType,
      isSearchAll,
      targetVariant,
      searchValue,
      sort,
      toggleSort,
      isMultiSelect,
      selectedIds,
      handleChangeSelectedIds,
      handleToggleSelectedId,
      filteredPages,
      selectableIds,
      selectedPages,
      isSelectionChanged,
      isLoading,
      isLoadingUsage,
      isError,
      isSourceEmpty,
      isFetchingNextPage,
      hasNextPage,
      handleFetchNextPage,
      handleReset,
      handleResetFilters,
      currentCampaignUsageByPageKey,
      otherCampaignTargets,
      handleRefetchUsage,
      removeConfirmState,
      removeConfirmDescription,
      isRemovingTemplate,
      handleOpenRemoveConfirm,
      handleCloseRemoveConfirm,
      handleConfirmRemove,
    ],
  );

  return <PickPageContext.Provider value={value}>{children}</PickPageContext.Provider>;
};
